import { type PluralizeInterface } from '@/types'
import { dayjs } from '@/utils'

/**
 * Removes all non-digit symbols from phone
 * @param {string} phone - input string
 * @returns {string}
 */
export const cleanPhone = (phone: string): string => `${phone.replace(/\D/g, '')}`

/**
 * Returns the value formatted as Russian currency
 * @param {number | string} value - money sum
 * @param {boolean} [hideCopecs=false] - option to hide copecs (default - false)
 * @returns {string}
 */
export const formatMoney = (value: number | string, hideCopecs?: boolean): string => {
    const copecks: string = hideCopecs ? '' : `,${value.toString().slice(-2)}`
    const rubles: string = value.toString().slice(0, -2)

    return `${new Intl.NumberFormat('ru-RU', {
        maximumFractionDigits: 2,
        minimumFractionDigits: 2,
        roundingMode: 'ceil',
        trailingZeroDisplay: 'stripIfInteger',
        useGrouping: 'always',
    }).format(Number(rubles))}${copecks} ₽`
}

/**
 * Returns phone in readable format
 * @param {string} phone - raw phone string
 * @returns {string}
 */
export const formatPhone = (phone: string): string => {
    if (phone.length === 11 && phone[0] === '7') {
        return phone.replace(/(\d{1})(\d{3})(\d{3})(\d{2})(\d{2})/, '+$1 $2 $3-$4-$5')
    }
    return phone
}

/**
 * Gets maximum user bitrh date from current date
 * @returns {Date}
 */
export const maxBirthDate = (): Date => dayjs().subtract(18, 'year').toDate()

/**
 * Gets minimum user bitrh date from current date
 * @returns {Date}
 */
export const minBirthDate = (): Date => dayjs().subtract(120, 'year').toDate()

/**
 * Returns correct russian word according to the quantity
 * @param options - options object: { 1: word_form_for_1, 2: word_form_for_2, 5: plural_word_form }
 * ex. { 1: 'символ', 2: 'символа', 5: 'символов' }
 * @param {number} quantity - quantity value
 * @return {string}
 */
export const pluralize = (options: PluralizeInterface, quantity: number): string => {
    let number: number = quantity % 100
    if (number >= 11 && number <= 19) {
        return options[5]
    }
    number = number % 10
    if (number === 1) {
        return options[1]
    } else if (number >= 2 && number <= 4) {
        return options[2]
    }
    return options[5]
}
